/**
 * 2D vector, in no particular reference frame.
 * The exact interpretation of the vector depends on usage.
 */
export type Vec2 = { x: number; y: number };

export const Vec2 = {
  ZERO: { x: 0, y: 0 },
  zero(): Vec2 {
    return { x: 0, y: 0 };
  },
  new(x: number, y: number): Vec2 {
    return { x, y };
  },
  scale(a: Vec2, val: number): Vec2 {
    return { x: a.x * val, y: a.y * val };
  },
  distanceSquare(a: Vec2, b: Vec2): number {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return dx * dx + dy * dy;
  },
  add(a: Vec2, b: Vec2): Vec2 {
    return { x: a.x + b.x, y: a.y + b.y };
  },
  sub(a: Vec2, b: Vec2): Vec2 {
    return { x: a.x - b.x, y: a.y - b.y };
  },
  norm(a: Vec2): number {
    return Math.sqrt(a.x * a.x + a.y * a.y);
  },
  normSquare(a: Vec2): number {
    return a.x * a.x + a.y * a.y;
  },
  FromArr(arr: [number, number]): Vec2 {
    return { x: arr[0], y: arr[1] };
  },
  min(a: Vec2, b: Vec2): Vec2 {
    return { x: Math.min(a.x, b.x), y: Math.min(a.y, b.y) };
  },
  max(a: Vec2, b: Vec2): Vec2 {
    return { x: Math.max(a.x, b.x), y: Math.max(a.y, b.y) };
  }
};

/**
 * Find two intersection points with another circle.
 * For perfectly tangent circles, both returned points are equal.
 *
 * Returns `null` if the circles are not overlapping.
 */
export function intersectCircles(
  aCenter: Vec2,
  aRadius: number,
  bCenter: Vec2,
  bRadius: number
): [Vec2, Vec2] | null {
  const d = Vec2.sub(bCenter, aCenter);
  const dnormSq = Vec2.normSquare(d);
  const radSum = aRadius + bRadius;
  // circles do not intersect externally
  if (dnormSq > radSum * radSum) {
    return null;
  }
  const dnorm = Math.sqrt(dnormSq);
  const invDnorm = 1.0 / dnorm;

  if (dnorm < Math.abs(aRadius - bRadius)) {
    // circles do not intersect internally
    return null;
  }

  const r0Sq = aRadius * aRadius;
  const r1Sq = bRadius * bRadius;

  // 'cross' is the point where the line through the circle
  // intersection points crosses the line between the circle centers

  // Determine the distance from c0 to cross.
  const a = (r0Sq - r1Sq + dnormSq) * invDnorm * 0.5;

  // Determine the coordinates of cross.
  const cross = Vec2.add(aCenter, Vec2.scale(d, a * invDnorm));

  // Determine the distance from cross to either of the intersection points.
  const h = Math.sqrt(r0Sq - a * a) * invDnorm;

  // Now determine the offsets of the intersection points from cross
  const delta = {
    x: -d.y * h,
    y: d.x * h
  };

  // Determine the absolute intersection points
  return [Vec2.add(cross, delta), Vec2.sub(cross, delta)];
}
